import { useSelector } from 'react-redux';

// The team-admin screens are shared by two organization kinds
// (specs/player-pwa-app/club-admin-league-role-plan.md §4):
//   agency — the original team_admin ("agent") organization
//   club   — one football club run by a club_admin
// Same components, different words. Everything a club admin reads comes from
// here so the two vocabularies never drift apart inside the JSX.

export const ORG_KIND = {
  AGENCY: 'agency',
  CLUB: 'club',
};

export const isClubKind = (kind) => kind === ORG_KIND.CLUB;

export const basePathForKind = (kind) => (isClubKind(kind) ? '/club-admin' : '/team-admin');

export const readOrgKindFromStorage = () => {
  try {
    return localStorage.getItem('organization_kind') || null;
  } catch (e) {
    return null;
  }
};

// Kind known before the organization endpoint answers (login payload →
// Redux → localStorage). Pages refine it with the loaded organization.
export const useOrgKind = () => {
  const fromStore = useSelector((state) => state.user?.organizationKind);
  return fromStore || readOrgKindFromStorage() || ORG_KIND.AGENCY;
};

export const vocabularyForKind = (kind, organizationName = '') => {
  if (isClubKind(kind)) {
    const club = organizationName || 'the club';
    return {
      kind: ORG_KIND.CLUB,
      roleTitle: 'Club admin',
      organizationNoun: 'Club',
      nav: { overview: 'Overview', players: 'Squad', settings: 'Settings' },
      rosterTitle: 'Squad',
      rosterSection: 'Players with app access',
      rosterEmpty: 'No players have app access yet. Invite your first player.',
      columns: { user: 'Email', player: 'Player', status: 'Status', date: 'Added' },
      notPicked: 'Not picked yet',
      notPickedHint: 'The player chooses their profile when they first sign in.',
      statusActive: 'Active',
      statusInactive: 'Removed',
      inviteButton: 'Invite player',
      inviteModalTitle: 'Invite a player',
      inviteHelp: `Enter the player's e-mail. They create their account from the link and pick their own player profile — you don't need to find them here.`,
      inviteSubmit: 'Send invitation',
      removeLabel: 'Remove',
      removeConfirm: (email) => `Remove ${email} from ${club}? They will lose access to the app immediately.`,
      revokeConfirm: (email) => `Revoke the invitation for ${email}?`,
      pendingSection: 'Pending invitations',
      pendingHint: 'Invitations not yet accepted',
      linkReady: 'Invitation link ready',
      linkHint: 'Share this link with the player if the e-mail does not arrive.',
      settingsTitle: 'Club settings',
      settingsNote: 'Club details are managed by SmrtStats. Contact support@smrtstats.com to change the club admin or the access period.',
      cards: {
        access: 'Players with app access',
        waiting: 'Waiting to pick their profile',
        waitingHint: 'Accepted the invitation, profile not chosen yet',
        pending: 'Pending invitations',
        period: 'Access',
        openEnded: 'Open-ended',
        until: (date) => `Until ${date}`,
        suspended: 'Suspended',
      },
      acceptIntro: (name) => `${name || 'Your club'} has invited you to the SmrtStats Player App.`,
      acceptOrgLabel: 'Club',
    };
  }

  const org = organizationName || 'the roster';
  return {
    kind: ORG_KIND.AGENCY,
    roleTitle: 'Team admin',
    organizationNoun: 'Organization',
    nav: { overview: 'Overview', players: 'Players', settings: 'Settings' },
    rosterTitle: 'Roster',
    rosterSection: 'Active roster',
    rosterEmpty: 'No players yet. Invite someone to get started.',
    columns: { user: 'User', player: 'Mapped to', status: 'Status', date: 'Mapped at' },
    notPicked: '—',
    notPickedHint: '',
    statusActive: 'Active',
    statusInactive: 'Inactive',
    inviteButton: 'Invite player',
    inviteModalTitle: 'Invite a player',
    inviteHelp: '',
    inviteSubmit: 'Send invitation',
    removeLabel: 'Remove',
    removeConfirm: (email) => `Remove ${email} from ${org}?`,
    revokeConfirm: (email) => `Revoke invitation for ${email}?`,
    pendingSection: 'Pending invitations',
    pendingHint: 'Magic links awaiting acceptance',
    linkReady: 'Invitation link ready',
    linkHint: 'Share this link if the email does not arrive.',
    settingsTitle: 'Organization settings',
    settingsNote: 'Edits to organization details are handled by SmrtStats support. Contact us to change plan or billing details.',
    cards: {
      access: 'Active mappings',
      waiting: 'Waiting to pick their profile',
      waitingHint: 'Accepted the invitation, profile not chosen yet',
      pending: 'Pending invitations',
      period: 'Plan',
      openEnded: 'Open-ended',
      until: (date) => `Until ${date}`,
      suspended: 'Suspended',
    },
    acceptIntro: (name) => (name ? `${name} has invited you to join SmrtStats.` : 'You have been invited to join SmrtStats.'),
    acceptOrgLabel: 'Organization',
  };
};
